import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Copy, Package, TriangleAlertIcon } from "@/components/icons";
import {
  Badge,
  Button,
  Checkbox,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Input,
  Label,
  Skeleton,
  toast,
} from "@/components/kit";
import { useProductCategoriesQuery } from "@/modules/products/presentation/api/queries";
import { formatLak } from "@/shared/lib/currency";
import {
  useDuplicateConvertBatchUnit,
  useDuplicateConvertPreview,
  useProductQuery,
} from "../api/queries";
import { ProductFormPageHeader } from "../ui/ProductFormPageHeader";
import { ProductFormSection } from "../ui/ProductFormSection";

type Search = { variantId?: string };

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export function DuplicateConvertUnitPage() {
  const { id } = useParams({ from: "/app/products/$id/duplicate-convert" });
  const search = useSearch({
    from: "/app/products/$id/duplicate-convert",
  }) as Search;
  const nav = useNavigate({ from: "/app/products/$id/duplicate-convert" });
  const product = useProductQuery(id);
  const categories = useProductCategoriesQuery();
  const duplicate = useDuplicateConvertBatchUnit();

  const sourceVariants = useMemo(
    () =>
      (product.data?.variants ?? []).filter(
        (variant) =>
          variant.isActive &&
          variant.variantType !== "bundle" &&
          !variant.isInventoryOnly &&
          !variant.inventoryVariantId,
      ),
    [product.data?.variants],
  );

  const initialSourceId =
    sourceVariants.find((variant) => variant.id === search.variantId)?.id ??
    sourceVariants[0]?.id ??
    "";
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>(
    initialSourceId ? [initialSourceId] : [],
  );

  const source =
    sourceVariants.find((variant) => variant.id === selectedVariantIds[0]) ??
    sourceVariants[0];
  const selectedCount = selectedVariantIds.length;
  const selectedVariants = sourceVariants.filter((variant) =>
    selectedVariantIds.includes(variant.id),
  );

  const [productName, setProductName] = useState("");
  const [variantName, setVariantName] = useState("");
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState("");
  const [stockUnitLabel, setStockUnitLabel] = useState("");
  const [receiveUnitLabel, setReceiveUnitLabel] = useState("");
  const [receiveConversionFactor, setReceiveConversionFactor] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("3");
  const [sourceQty, setSourceQty] = useState("");
  const [targetPerSource, setTargetPerSource] = useState("1");
  const [moveStock, setMoveStock] = useState(true);
  const [deactivateSource, setDeactivateSource] = useState(true);
  const [note, setNote] = useState("");

  const initializedKey = `${product.data?.id ?? ""}:${source?.id ?? ""}`;
  const [lastInitializedKey, setLastInitializedKey] = useState("");
  useEffect(() => {
    if (selectedVariantIds.length === 0 && initialSourceId) {
      setSelectedVariantIds([initialSourceId]);
    }
  }, [initialSourceId, selectedVariantIds.length]);

  useEffect(() => {
    if (!product.data || !source || lastInitializedKey === initializedKey) {
      return;
    }
    setProductName(`${product.data.name} (${source.stockUnitLabel} → ໜ່ວຍໃໝ່)`);
    setVariantName(source.name);
    setSku(source.sku ? `${source.sku}-NEW` : "");
    setPrice(String(Math.max(0, Math.round(Number(source.price) || 0))));
    setStockUnitLabel(source.stockUnitLabel);
    setReceiveUnitLabel(source.receiveUnitLabel ?? "");
    setReceiveConversionFactor(
      Number(source.receiveConversionFactor) > 1
        ? String(Number(source.receiveConversionFactor))
        : "",
    );
    setLowStockThreshold(String(source.lowStockThreshold ?? 3));
    setSourceQty(String(source.stockQuantity));
    setLastInitializedKey(initializedKey);
  }, [initializedKey, lastInitializedKey, product.data, source]);

  const previewInput =
    moveStock &&
    selectedVariantIds.length === 1 &&
    selectedVariantIds[0] &&
    Number(sourceQty) > 0 &&
    Number(targetPerSource) > 0
      ? {
          sourceVariantId: selectedVariantIds[0],
          sourceQuantityToMove: Number(sourceQty),
          targetQuantityPerSource: Number(targetPerSource),
        }
      : null;
  const preview = useDuplicateConvertPreview(previewInput);

  const defaultCategoryIds = useMemo(
    () =>
      product.data?.categories.map((category) => category.id) ??
      (categories.data?.data[0]?.id ? [categories.data.data[0].id] : []),
    [product.data?.categories, categories.data?.data],
  );

  const [targetCategoryIds, setTargetCategoryIds] =
    useState<string[]>(defaultCategoryIds);
  useEffect(() => {
    if (defaultCategoryIds.length > 0 && targetCategoryIds.length === 0) {
      setTargetCategoryIds(defaultCategoryIds);
    }
  }, [defaultCategoryIds, targetCategoryIds.length]);

  async function submit() {
    if (!product.data || !source) return;
    if (selectedVariants.length === 0) {
      toast.error("ກະລຸນາເລືອກຕົວເລືອກຢ່າງນ້ອຍ 1 ລາຍການ");
      return;
    }
    if (!productName.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ສິນຄ້າໃໝ່");
      return;
    }
    if (targetCategoryIds.length === 0) {
      toast.error("ກະລຸນາເລືອກຢ່າງນ້ອຍ 1 ຫມວດຫມູ່");
      return;
    }
    if (moveStock && selectedVariants.length === 1 && !preview.data) {
      toast.error("ລໍຖ້າຕົວຢ່າງກ່ອນຢືນຢັນ");
      return;
    }

    try {
      const result = await duplicate.mutateAsync({
        targetProduct: {
          name: productName.trim(),
          categoryIds: targetCategoryIds,
          description: product.data.description ?? "",
          imageKey: product.data.imageKey,
        },
        variants: selectedVariants.map((variant) => ({
          sourceVariantId: variant.id,
          targetVariant: {
            name:
              selectedVariants.length === 1
                ? variantName.trim() || variant.name
                : variant.name,
            sku:
              selectedVariants.length === 1
                ? sku.trim() || null
                : variant.sku
                  ? `${variant.sku}-NEW`
                  : null,
            price:
              selectedVariants.length === 1
                ? Number(price) || 0
                : Number(variant.price) || 0,
            stockUnitLabel: stockUnitLabel.trim() || variant.stockUnitLabel,
            receiveUnitLabel: receiveUnitLabel.trim() || null,
            receiveConversionFactor:
              receiveUnitLabel.trim() && Number(receiveConversionFactor) > 0
                ? Number(receiveConversionFactor)
                : undefined,
            lowStockThreshold:
              Number(lowStockThreshold) || variant.lowStockThreshold,
          },
          moveStock,
          sourceQuantityToMove:
            selectedVariants.length === 1
              ? Number(sourceQty) || 1
              : variant.stockQuantity,
          targetQuantityPerSource: Number(targetPerSource) || 1,
          deactivateSourceVariant: deactivateSource,
        })),
        note,
      });
      toast.success("ຄັດລອກ ແລະ ແປງໜ່ວຍສຳເລັດ");
      nav({
        to: "/app/products/$id/stock",
        params: { id: result.product.id },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "ດຳເນີນການລົ້ມເຫຼວ");
    }
  }

  if (product.isLoading) {
    return (
      <>
        <Header />
        <Main className="mx-auto flex max-w-5xl flex-col gap-4">
          <Skeleton className="h-12 w-64 rounded-xl" />
          <Skeleton className="h-96 w-full rounded-2xl" />
        </Main>
      </>
    );
  }

  if (!product.data || !source) {
    return (
      <>
        <Header />
        <Main>
          <div className="brand-card p-8">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>ບໍ່ພົບຕົວເລືອກທີ່ແປງໜ່ວຍໄດ້</EmptyTitle>
                <EmptyDescription>
                  ຟັງຊັນນີ້ຮອງຮັບສະເພາະຕົວເລືອກປົກກະຕິ ທີ່ບໍ່ແມ່ນເຊັດ ຫຼື ຄລັງຮ່ວມ ໃນ v1
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto w-full max-w-5xl">
          <ProductFormPageHeader
            title="ຄັດລອກ ແລະ ແປງໜ່ວຍຂາຍ"
            subtitle="ສ້າງສິນຄ້າໃໝ່ຈາກສິນຄ້າເກົ່າ ພ້ອມຍ້າຍສະຕັອກ/ລັອດໄດ້"
            onBack={() =>
              nav({ to: "/app/products/$id/stock", params: { id } })
            }
          />

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
            <div className="flex flex-col gap-6">
              <ProductFormSection
                icon={Package}
                title="ສິນຄ້າຕົ້ນທາງ"
                description="ສິນຄ້າເກົ່າທີ່ຈະໃຊ້ເປັນຕົ້ນສະບັບ"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full border-secondary/40"
                    onClick={() =>
                      setSelectedVariantIds(
                        sourceVariants.map((variant) => variant.id),
                      )
                    }
                  >
                    ເລືອກທັງໝົດ
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full border-secondary/40"
                    onClick={() => setSelectedVariantIds([])}
                  >
                    ລ້າງທີ່ເລືອກ
                  </Button>
                  <Badge variant="outline" className="rounded-full">
                    {selectedCount} / {sourceVariants.length} ລາຍການ
                  </Badge>
                </div>

                <div className="flex flex-col gap-2">
                  {sourceVariants.map((variant) => {
                    const selected = selectedVariantIds.includes(variant.id);
                    return (
                      <label
                        key={variant.id}
                        htmlFor={`source-variant-${variant.id}`}
                        className="flex cursor-pointer items-start gap-3 rounded-xl border border-secondary/15 bg-background/60 p-3"
                      >
                        <Checkbox
                          id={`source-variant-${variant.id}`}
                          checked={selected}
                          onCheckedChange={(checked) => {
                            setSelectedVariantIds((current) => {
                              if (checked) {
                                return current.includes(variant.id)
                                  ? current
                                  : [...current, variant.id];
                              }
                              return current.filter((id) => id !== variant.id);
                            });
                          }}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium">{variant.name}</p>
                            <Badge variant="outline" className="rounded-full">
                              {variant.stockQuantity} {variant.stockUnitLabel}
                            </Badge>
                          </div>
                          <p className="mt-1 text-muted-foreground text-xs">
                            {formatLak(variant.price)}/{variant.stockUnitLabel}
                            {variant.sku ? ` · SKU ${variant.sku}` : ""}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </ProductFormSection>

              <ProductFormSection
                icon={Copy}
                title="ສິນຄ້າປາຍທາງ"
                description="ຂໍ້ມູນສິນຄ້າໃໝ່ທີ່ຈະສ້າງ"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="ຊື່ສິນຄ້າໃໝ່">
                    <Input
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                    />
                  </Field>
                  <Field label="ໝວດໝູ່">
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(categories.data?.data ?? []).map((category) => {
                        const selected = targetCategoryIds.includes(
                          category.id,
                        );
                        return (
                          <label
                            key={category.id}
                            htmlFor={`target-category-${category.id}`}
                            className="flex cursor-pointer items-center gap-2 rounded-xl border border-secondary/15 bg-background/60 px-3 py-2"
                          >
                            <Checkbox
                              id={`target-category-${category.id}`}
                              checked={selected}
                              onCheckedChange={(checked) => {
                                if (checked === true) {
                                  setTargetCategoryIds((current) => [
                                    ...current,
                                    category.id,
                                  ]);
                                  return;
                                }
                                setTargetCategoryIds((current) =>
                                  current.filter((id) => id !== category.id),
                                );
                              }}
                            />
                            <span className="text-sm">{category.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </Field>
                  {selectedCount <= 1 ? (
                    <>
                      <Field label="ຊື່ຕົວເລືອກໃໝ່">
                        <Input
                          value={variantName}
                          onChange={(e) => setVariantName(e.target.value)}
                        />
                      </Field>
                      <Field label="SKU ໃໝ່">
                        <Input
                          value={sku}
                          onChange={(e) => setSku(e.target.value)}
                        />
                      </Field>
                      <Field label="ລາຄາຕໍ່ໜ່ວຍໃໝ່">
                        <Input
                          type="number"
                          min={0}
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                        />
                      </Field>
                    </>
                  ) : (
                    <div className="rounded-xl border border-secondary/15 bg-background/60 p-3 text-muted-foreground text-sm sm:col-span-2">
                      ເມື່ອເລືອກຫຼາຍຕົວເລືອກ ລະບົບຈະຄັດລອກຊື່, SKU ແລະ
                      ລາຄາຈາກແຕ່ລະຕົວເລືອກເກົ່າ
                    </div>
                  )}
                  <Field label="ໜ່ວຍຂາຍໃໝ່">
                    <Input
                      value={stockUnitLabel}
                      onChange={(e) => setStockUnitLabel(e.target.value)}
                    />
                  </Field>
                  <Field label="ໜ່ວຍຮັບເຂົ້າ (ທາງເລືອກ)">
                    <Input
                      value={receiveUnitLabel}
                      onChange={(e) => setReceiveUnitLabel(e.target.value)}
                    />
                  </Field>
                  <Field label="ອັດຕາແປງຮັບເຂົ້າ">
                    <Input
                      type="number"
                      step="any"
                      min={0}
                      value={receiveConversionFactor}
                      onChange={(e) =>
                        setReceiveConversionFactor(e.target.value)
                      }
                    />
                  </Field>
                </div>
              </ProductFormSection>

              <ProductFormSection
                icon={TriangleAlertIcon}
                title="ການແປງໜ່ວຍ"
                description="ກຳນົດຈຳນວນທີ່ຈະຍ້າຍ ແລະ ອັດຕາແປງ"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  {selectedCount <= 1 ? (
                    <Field label={`ຈຳນວນ ${source.stockUnitLabel} ທີ່ຈະຍ້າຍ`}>
                      <Input
                        type="number"
                        min={1}
                        value={sourceQty}
                        onChange={(e) => setSourceQty(e.target.value)}
                      />
                    </Field>
                  ) : (
                    <div className="rounded-xl border border-secondary/15 bg-background/60 p-3 text-muted-foreground text-sm">
                      ຈະຍ້າຍສະຕັອກຄົງເຫຼືອທັງໝົດຂອງແຕ່ລະຕົວເລືອກ
                    </div>
                  )}
                  <Field label="ຈຳນວນໜ່ວຍໃໝ່ຕໍ່ 1 ໜ່ວຍເກົ່າ">
                    <Input
                      type="number"
                      min={0.0001}
                      step="any"
                      value={targetPerSource}
                      onChange={(e) => setTargetPerSource(e.target.value)}
                    />
                  </Field>
                  <Field label="ເຕືອນໃກ້ໝົດ">
                    <Input
                      type="number"
                      min={0}
                      value={lowStockThreshold}
                      onChange={(e) => setLowStockThreshold(e.target.value)}
                    />
                  </Field>
                  <Field label="ໝາຍເຫດ">
                    <Input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </Field>
                </div>
                <div className="flex flex-col gap-2 rounded-xl border border-secondary/15 bg-background/60 p-3 text-sm">
                  <label
                    htmlFor="move-stock"
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <Checkbox
                      id="move-stock"
                      checked={moveStock}
                      onCheckedChange={(checked) =>
                        setMoveStock(Boolean(checked))
                      }
                    />
                    ຍ້າຍສະຕັອກ/ລັອດຄົງເຫຼືອໄປສິນຄ້າໃໝ່
                  </label>
                  <label
                    htmlFor="deactivate-source"
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <Checkbox
                      id="deactivate-source"
                      checked={deactivateSource}
                      onCheckedChange={(checked) =>
                        setDeactivateSource(Boolean(checked))
                      }
                    />
                    ປິດຕົວເລືອກເກົ່າຫຼັງສຳເລັດ
                  </label>
                </div>
              </ProductFormSection>
            </div>

            <ProductFormSection
              icon={Package}
              title="ຕົວຢ່າງກ່ອນຢືນຢັນ"
              description="ກວດລັອດ ແລະ ຕົ້ນທຶນກ່ອນຢືນຢັນ"
              className="lg:sticky lg:top-4"
            >
              {!moveStock ? (
                <p className="text-muted-foreground text-sm">
                  ຈະສ້າງສິນຄ້າໃໝ່ໂດຍບໍ່ຍ້າຍສະຕັອກ
                </p>
              ) : selectedCount > 1 ? (
                <div className="flex flex-col gap-3">
                  <p className="text-muted-foreground text-sm">
                    ຈະສ້າງ {selectedCount} ຕົວເລືອກໃໝ່
                    ໂດຍຍ້າຍສະຕັອກຄົງເຫຼືອທັງໝົດຂອງແຕ່ລະລາຍການ
                  </p>
                  <ul className="flex flex-col gap-2">
                    {selectedVariants.map((variant) => {
                      const targetQty =
                        variant.stockQuantity * (Number(targetPerSource) || 0);
                      return (
                        <li
                          key={variant.id}
                          className="rounded-xl border border-secondary/15 bg-background/60 p-3 text-sm"
                        >
                          <p className="font-medium">{variant.name}</p>
                          <p className="text-muted-foreground">
                            {variant.stockQuantity} {variant.stockUnitLabel} →{" "}
                            {Number.isInteger(targetQty)
                              ? targetQty
                              : targetQty.toFixed(2)}{" "}
                            {stockUnitLabel || "ໜ່ວຍໃໝ່"}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="rounded-xl border border-amber-600/20 bg-amber-500/5 p-3 text-amber-900 text-sm">
                    ລະບົບຈະກວດ lot ແລະ ຕົ້ນທຶນອີກຄັ້ງຕອນຢືນຢັນ
                  </p>
                </div>
              ) : preview.isLoading ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-16 rounded-xl" />
                  <Skeleton className="h-16 rounded-xl" />
                </div>
              ) : preview.isError ? (
                <p className="rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-destructive text-sm">
                  {preview.error instanceof Error
                    ? preview.error.message
                    : "ໂຫຼດຕົວຢ່າງບໍ່ສຳເລັດ"}
                </p>
              ) : preview.data ? (
                <div className="flex flex-col gap-3">
                  <div className="grid gap-2">
                    <Badge variant="outline" className="w-fit rounded-full">
                      {preview.data.sourceStockBefore} →{" "}
                      {preview.data.sourceStockAfter} {source.stockUnitLabel}
                    </Badge>
                    <Badge className="w-fit rounded-full border-leaf/30 bg-leaf/10 text-leaf">
                      +{preview.data.targetQuantity} {stockUnitLabel || "ໜ່ວຍໃໝ່"}
                    </Badge>
                  </div>
                  <div className="rounded-xl border border-secondary/15 bg-background/60 p-3">
                    <p className="text-muted-foreground text-xs">ຕົ້ນທຶນຕໍ່ໜ່ວຍໃໝ່</p>
                    <p className="font-semibold text-lg">
                      {formatLak(preview.data.unitLandedCost)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      ລວມ {formatLak(preview.data.totalLandedCost)}
                    </p>
                  </div>
                  {preview.data.warnings.map((warning) => (
                    <p
                      key={warning.code}
                      className="rounded-xl border border-amber-600/20 bg-amber-500/5 p-3 text-amber-900 text-sm"
                    >
                      {warning.message}
                    </p>
                  ))}
                  <ul className="flex flex-col gap-2">
                    {preview.data.lotPicks.map((pick) => (
                      <li
                        key={pick.stockLotId}
                        className="rounded-xl border border-secondary/15 bg-background/60 p-3 text-sm"
                      >
                        <p className="font-mono text-xs">{pick.stockLotId}</p>
                        <p className="text-muted-foreground">
                          {pick.quantity} × {formatLak(pick.unitLandedCost)} ={" "}
                          {formatLak(pick.lineCost)}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <Button
                className="mt-2 w-full rounded-full"
                isLoading={duplicate.isPending}
                disabled={
                  selectedCount === 0 ||
                  (moveStock &&
                    selectedCount === 1 &&
                    (!preview.data || preview.isError))
                }
                onClick={submit}
              >
                ຢືນຢັນຄັດລອກ ແລະ ແປງໜ່ວຍ
              </Button>
            </ProductFormSection>
          </div>
        </div>
      </Main>
    </>
  );
}
